import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import BottomNav from './BottomNav';
import CoachFab from './CoachFab';
import { SkipLink } from './SkipLink';
import { PwaInstallPrompt } from './PwaInstallPrompt';
import { theme } from '../styles/GlobalStyles';
import { useAuth } from '../context/AuthContext';

const Shell = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--color-background);
  color: var(--color-text);
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const MainWrapper = styled.div`
  flex: 1;
  display: flex;
  padding-top: 80px;
  min-height: calc(100dvh - 80px);
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    padding-top: 70px;
    min-height: calc(100dvh - 70px);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: ${theme.spacing['4xl']} ${theme.spacing.xl};
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing['3xl']} ${theme.spacing.md};
    padding-bottom: calc(${theme.spacing['3xl']} + 100px);
  }
`;

const ProfileBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: color-mix(in srgb, var(--color-secondary) 15%, transparent);
  border: 1px solid var(--color-secondary-light);
  margin-bottom: ${theme.spacing['2xl']};
  flex-wrap: wrap;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  font-family: 'Roboto', sans-serif;
  color: var(--color-text);
  strong {
    font-family: 'Inter', sans-serif;
    font-size: ${theme.fontSize.lg};
    color: var(--color-primary);
  }
`;

const BannerButton = styled.button`
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  font-size: ${theme.fontSize.sm};
  font-family: 'Inter', sans-serif;
  transition: all 0.3s ease;
  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
`;

const Footer = styled.footer`
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  color: var(--color-text-muted);
  padding: ${theme.spacing.md};
  text-align: center;
  font-size: ${theme.fontSize.sm};
  font-family: 'Roboto', sans-serif;
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
`;

type LayoutProps = { children: React.ReactNode };

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const needsProfile = Boolean(user && !user.hasCompletedProfile);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Shell>
      <SkipLink href="#main-content">Pular para o conteúdo principal</SkipLink>
      <Header>
        <Navbar />
      </Header>
      <MainWrapper>
        <MainContent id="main-content" tabIndex={-1}>
          {needsProfile && (
            <ProfileBanner role="status" aria-live="polite">
              <BannerText>
                <strong>Complete seu perfil</strong>
                <span>Finalize suas preferências para liberar treinos totalmente personalizados.</span>
              </BannerText>
              <BannerButton type="button" onClick={() => navigate('/profile')}>
                Configurar perfil
              </BannerButton>
            </ProfileBanner>
          )}
          {children}
        </MainContent>
      </MainWrapper>
      <CoachFab />
      <Footer>
        TITAN MODE © {new Date().getFullYear()}
      </Footer>
      <BottomNav />
      <PwaInstallPrompt />
    </Shell>
  );
};
